import type React from 'react'
import type { DashboardMetric } from '../../../redux/services/coreApi'
import { formatMetricValue, percentChange } from './dashboardFormat'
import { useCountUp } from './useCountUp'
import { useFitText } from './useFitText'

interface HeroStatProps {
  metric: DashboardMetric
  /** Accent class pair for the value + glow, so the tiles aren't a wall of one colour. */
  accent: string
  /** Display currency for money-unit metrics (e.g. "DKK", "EUR"). */
  currency: string
}

const HeroStat: React.FC<HeroStatProps> = ({ metric, accent, currency }) => {
  const animated = useCountUp(metric.value)

  // Dense tiles carry a "this period" figure: show "+<n> this week" with a week-over-week trend arrow.
  const hasSecondary = metric.secondary != null
  const change = hasSecondary
    ? percentChange(metric.secondary as number, metric.secondaryPrevious)
    : null
  const isUp = change == null || change >= 0

  // Currency lives in the label (e.g. "Volume traded (DKK)") so the number stays clean.
  const label =
    metric.unit === 'money' ? `${metric.label} (${currency})` : metric.label

  // Shrink the headline font to one line when the value is still too wide for the tile (1080p), instead
  // of letting it wrap or clip. Measured against the final value so it doesn't jitter mid-count.
  const finalValue = formatMetricValue(metric.value)
  const { containerRef, measureRef, fontSizePx } = useFitText(finalValue)

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm xl:p-6">
      {/* soft accent glow in the corner */}
      <div
        className={`pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full blur-2xl opacity-20 ${accent}`}
        aria-hidden
      />
      <p className="text-xs font-semibold uppercase tracking-widest text-white/45 xl:text-sm">
        {label}
      </p>
      <div ref={containerRef} className="relative mt-2">
        {/* Full-size, hidden reference used only to measure the natural width. */}
        <span
          ref={measureRef}
          aria-hidden
          className="pointer-events-none invisible absolute left-0 top-0 whitespace-nowrap font-bold tabular-nums text-4xl xl:text-6xl"
        >
          {finalValue}
        </span>
        <p
          className={`whitespace-nowrap font-bold tabular-nums leading-none text-transparent bg-clip-text ${accent} text-4xl xl:text-6xl`}
          style={fontSizePx != null ? { fontSize: `${fontSizePx}px` } : undefined}
        >
          {formatMetricValue(animated)}
        </p>
      </div>
      {hasSecondary ? (
        <p
          className={`mt-3 flex flex-wrap items-center gap-x-2 text-sm font-semibold xl:text-base ${
            isUp ? 'text-emerald-400' : 'text-rose-400'
          }`}
        >
          <span className="flex items-center gap-1">
            <span aria-hidden>{isUp ? '▲' : '▼'}</span>+
            {formatMetricValue(metric.secondary as number)}
          </span>
          <span className="font-normal text-white/35">
            {metric.secondaryLabel ?? 'this week'}
            {change != null &&
              ` · ${Math.abs(change).toFixed(Math.abs(change) >= 10 ? 0 : 1)}%`}
          </span>
        </p>
      ) : (
        metric.caption && (
          <p className="mt-3 text-sm font-normal text-white/35 xl:text-base">
            {metric.caption}
          </p>
        )
      )}
    </div>
  )
}

export default HeroStat
